// MAIN_APP adapter for the canonical wallet billing API.
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { devLogApiError } from '../utils/devLogApiError';
import { billingApiPath } from '../utils/billingApiPaths';

const BillingContext = createContext();

export const BillingProvider = ({ children, axios }) => {
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [dataScope, setDataScope] = useState(null);
  const scope = useSelector((state) => {
    const auth = state.auth;
    return auth?.isAuthenticated && auth.bootstrapStatus === 'ready' && auth.user?.id && auth.company?.id
      ? String(auth.user.id) + ':' + String(auth.company.id) : null;
  });
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const cacheRef = useRef({ scope: null, pricing: {}, lastUpdate: null });
  const requestRef = useRef(0);

  const loadBillingData = useCallback(async (force = false) => {
    const requestScope = scopeRef.current;
    if (!axios || !requestScope) return;
    if (!force && cacheRef.current.scope === requestScope && cacheRef.current.lastUpdate) return;
    const requestId = ++requestRef.current;
    const isCurrent = () => scopeRef.current === requestScope && requestRef.current === requestId;
    setLoading(true);
    setError(null);
    try {
      const config = { errorPresentation: 'inline', cacheTtlMs: 0, noDedup: true };
      const [balanceResult, usageResult] = await Promise.allSettled([
        axios.get(billingApiPath('balance'), config),
        axios.get(billingApiPath('resources-usage'), config),
      ]);
      if (!isCurrent()) return;
      const balanceResponse = balanceResult.status === 'fulfilled' ? balanceResult.value : null;
      const usageResponse = usageResult.status === 'fulfilled' ? usageResult.value : null;
      setDataScope(requestScope);
      if (balanceResponse?.data?.success === true) setBalance(balanceResponse.data.data);
      if (usageResponse?.data?.success === true) setUsage(usageResponse.data);
      if (balanceResponse?.data?.success === true && usageResponse?.data?.success === true) {
        const timestamp = Date.now();
        cacheRef.current = { ...cacheRef.current, scope: requestScope, lastUpdate: timestamp };
        setLastUpdate(timestamp);
      } else {
        setError('Не удалось обновить данные оплаты. Повторите попытку.');
      }
    } catch (failure) {
      if (isCurrent()) {
        setError('Не удалось обновить данные оплаты. Повторите попытку.');
        devLogApiError('контекст биллинга: загрузка', failure);
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [axios]);

  const loadResourcePricing = useCallback(async (resourceType, force = false) => {
    const requestScope = scopeRef.current;
    if (!axios || !requestScope || !resourceType) return null;
    if (!force && cacheRef.current.scope === requestScope && cacheRef.current.pricing[resourceType]) {
      return cacheRef.current.pricing[resourceType].data;
    }
    try {
      const response = await axios.get(billingApiPath('resource-pricing/' + encodeURIComponent(resourceType)), {
        errorPresentation: 'inline', cacheTtlMs: 0, noDedup: true,
      });
      if (scopeRef.current !== requestScope || response?.data?.success !== true) return null;
      const entry = { data: response.data.data, timestamp: Date.now() };
      const cached = cacheRef.current.scope === requestScope ? cacheRef.current.pricing : {};
      cacheRef.current = { ...cacheRef.current, scope: requestScope, pricing: { ...cached, [resourceType]: entry } };
      setDataScope(requestScope);
      setPricing(cacheRef.current.pricing);
      return entry.data;
    } catch (failure) {
      if (scopeRef.current === requestScope) devLogApiError('контекст биллинга: цена ресурса', failure);
      return null;
    }
  }, [axios]);

  const refresh = useCallback(() => loadBillingData(true), [loadBillingData]);

  useEffect(() => {
    scopeRef.current = scope;
    requestRef.current += 1;
    cacheRef.current = { scope, pricing: {}, lastUpdate: null };
    setDataScope(scope);
    setBalance(null);
    setUsage(null);
    setPricing({});
    setLastUpdate(null);
    setLoading(false);
    setError(null);
    if (scope) loadBillingData();
  }, [scope, loadBillingData]);

  useEffect(() => () => { scopeRef.current = null; requestRef.current += 1; }, []);

  const current = Boolean(scope && dataScope === scope);
  return (
    <BillingContext.Provider value={{
      balance: current ? balance : null,
      usage: current ? usage : null,
      pricing: current ? pricing : {},
      loading: current && loading,
      lastUpdate: current ? lastUpdate : null,
      error: current ? error : null,
      loadResourcePricing, refresh, loadBillingData,
    }}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) throw new Error('useBilling используйте только внутри BillingProvider');
  return context;
};
